import { createContext, ReactNode, useContext, useMemo } from 'react';

import type { Candidate } from 'model/Candidate/Candidate';
import { useIndeedConfig } from 'shared/legacy/hooks/useBrowserExtension/indeed/useIndeedConfig';
import useFeatureToggles, { FeatureToggleType } from 'shared/legacy/hooks/useFeatureToggles';
import { CommAgentFlow, CommAgentFlowGates, getCommAgentFlow, isRjCommHubChannelEnabled } from './getCommAgentFlow';

type CommAgentFlowContextValue = {
  flow: CommAgentFlow;
};

const CommAgentFlowContext = createContext<CommAgentFlowContextValue | null>(null);

type CommAgentFlowProviderProps = {
  candidate: Candidate;
  children: ReactNode;
};

const useCommAgentFlowGates = (): CommAgentFlowGates => {
  const { isFeatureToggleEnabled } = useFeatureToggles();
  const { isLoggedIn: isIndeedLoggedIn } = useIndeedConfig();

  return {
    isIndeedCommAgentFlowEnabled: isFeatureToggleEnabled(FeatureToggleType.INDEED_COMMUNICATION_AGENT),
    isRJCommHubEnabled: isFeatureToggleEnabled(FeatureToggleType.RJ_COMMUNICATION_HUB),
    isWaCommunicationAgentEnabled: isFeatureToggleEnabled(FeatureToggleType.WA_COMMUNICATION_AGENT),
    isIndeedLoggedIn,
  };
};

export const useResolveCommAgentFlow = (candidate: Candidate | null | undefined): CommAgentFlow | null => {
  const gates = useCommAgentFlowGates();

  return getCommAgentFlow(candidate, gates);
};

export const useResolveRjCommHubChannelEnabled = (candidate: Candidate | null | undefined): boolean => {
  const gates = useCommAgentFlowGates();

  return isRjCommHubChannelEnabled(candidate, gates);
};

export const CommAgentFlowProvider = ({ candidate, children }: CommAgentFlowProviderProps) => {
  const flow = useResolveCommAgentFlow(candidate);
  const value = useMemo<CommAgentFlowContextValue | null>(() => (flow ? { flow } : null), [flow]);

  if (!value) return null;

  return <CommAgentFlowContext.Provider value={value}>{children}</CommAgentFlowContext.Provider>;
};

export const useCommAgentFlow = (): CommAgentFlow => {
  const ctx = useContext(CommAgentFlowContext);
  if (!ctx) {
    throw new Error('useCommAgentFlow must be used within a CommAgentFlowProvider');
  }
  return ctx.flow;
};

export const useIsIndeedFlow = (): boolean => {
  return useCommAgentFlow() === 'indeed';
};

export const useIsWhatsAppOnlyFlow = (): boolean => {
  return useCommAgentFlow() === 'whatsapp';
};
